import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getFilteredData } from "../api/filters";
import { hasActiveFilters } from "../constants/subgroup-filters";

/**
 * Applies the subgroup filters to the selected quarter of a series.
 *
 * `series` is the array of {time, data} the dashboard already holds for the
 * level (country, province or hospital). When filters are active the selected
 * quarter is recomputed server side and swapped into a derived copy of the
 * series; every other quarter stays as it was. With no filters the series is
 * returned untouched.
 */
export const useFilteredQuarter = ({ level, code, series }) => {
  const filters = useSelector((state) => state?.data?.subgroupFilters) || {};
  const currentQuarter = useSelector((state) => state?.data?.currentQuarter) || 0;
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const active = hasActiveFilters(filters);
  const quarter = series?.[currentQuarter]?.time;
  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    if (!active || !quarter || (level !== "country" && (code === undefined || code === null || code === -1))) {
      setResult(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getFilteredData({ level, code, quarter, filters })
      .then((response) => {
        if (cancelled) return;
        setResult(response?.status === "successful" ? response : null);
      })
      .catch(() => {
        if (!cancelled) setResult(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, quarter, level, code, filterKey]);

  const filteredSeries = useMemo(() => {
    if (!active || !result || !series || result.time !== quarter) return series;
    return series.map((entry, index) => (index === currentQuarter ? { ...entry, data: result.data } : entry));
  }, [active, result, series, quarter, currentQuarter]);

  return {
    active,
    loading,
    series: filteredSeries,
    applied: result?.filters || [],
    sampleSize: result?.sample_size ?? null,
    sampleSizePediatric: result?.sample_size_pediatric ?? null,
    empty: Boolean(active && result && result.empty),
  };
};
